import {
  TextField,
  InputAdornment,
  Box,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useRef, useState } from 'react';

import searchIcon from '../../../assets/images/faqImage/search.svg';
import cancel from '../../../assets/images/knownIssue/cancelIcon.svg';

const KnownIssuesSearch = ({ value, onChange, staticContent, onSearch }) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:900px)');

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (!value) {
      setIsFocused(false);
    }
  };

  const handleClearSearch = () => {
    onChange({ target: { value: '' } });
    setIsFocused(false);
    if (inputRef.current) inputRef.current.blur();
    if (onSearch) onSearch('');
  };

  const handleSearchClick = () => {
    if (onSearch) {
      // If the search box is empty, clear the search in parent
      if (!value.trim()) {
        onSearch('');
      } else {
        onSearch(value);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchClick();
    }
  };

  return (
    <section aria-label="Find known issues">
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 1,
          width: '100%'
        }}
      >
        <TextField
          id="known-issues-search"
          label={staticContent.searchLabel}
          variant="standard"
          value={value}
          inputRef={inputRef}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          sx={{
            mb: isMobile ? 1.5 : 0,
            minWidth: '272px',
            width: { xs: '100%', sm: '80%', md: '80%', lg: '272px' }
          }}
          InputLabelProps={{
            htmlFor: 'known-issue-search',
            shrink: isFocused || Boolean(value),
            style: {
              marginLeft: '30px',
              fontWeight: isFocused || value ? 400 : 600,
              color: isFocused || value ? '#717173' : '#000000',
              transition: 'all 0.3s ease-in-out'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img
                  src={searchIcon}
                  alt="search"
                  style={{
                    width: 20,
                    height: 20,
                    marginBottom: '5px'
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: value && (
              <InputAdornment position="end">
                <img
                  src={cancel}
                  alt="clear search"
                  tabIndex={0}
                  role="button"
                  aria-label="Clear search input"
                  style={{
                    width: 10,
                    height: 10,
                    marginBottom: '5px',
                    marginRight: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={handleClearSearch}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleClearSearch();
                    }
                  }}
                />
              </InputAdornment>
            )
          }}
        />
        {/* Search button for desktop/tablet */}
        {(isTablet || !isMobile) && (
          <Button
            variant="primary"
            size="md"
            onClick={handleSearchClick}
            aria-label="Search"
          >
            {staticContent.searchLabel}
          </Button>
        )}
      </Box>
      {/* Search button for mobile */}
      {isMobile && (
        <Button
          variant="primary"
          size="md"
          sx={{
            '@media(max-width: 600px)': {
              mb:"10px"
              // maxWidth: 'fit-content'
            },
          }}
          onClick={handleSearchClick}
          aria-label="Search"
        >
          {staticContent.searchButtonLabel || 'Search'}
        </Button>
      )}
    </section>
  );
};

export default KnownIssuesSearch;
